import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { Loader2 } from "lucide-react";
import { toast } from "sonner";
import { EntityForm } from "@/components/v2/entity-form";
import { EmptyState } from "@/components/v2/empty-state";
import { useCreateOpportunity, useCustomers } from "@/hooks/use-api";
import {
  opportunityFields,
  type OpportunityFormValues,
} from "./opportunity-form-config";

export default function V2OpportunityCreatePage() {
  const navigate = useNavigate();
  const createOpportunity = useCreateOpportunity();
  const customersQuery = useCustomers({ limit: 1000 });
  const [error, setError] = useState<string | null>(null);

  const customerOptions = (
    (
      customersQuery.data as
        | { customers?: Record<string, unknown>[] }
        | undefined
    )?.customers ?? []
  ).map((c) => ({
    value: String(c.contact_role_id ?? ""),
    label:
      [c.first_name, c.last_name].filter(Boolean).join(" ") ||
      String(c.company_name ?? ""),
  }));

  async function onSubmit(values: OpportunityFormValues) {
    setError(null);
    try {
      const { opportunity } = await createOpportunity.mutateAsync(
        values as Record<string, unknown>,
      );
      const id = (opportunity as Record<string, unknown> | undefined)?.id;
      if (!id) {
        setError("Unexpected response from the server — please try again.");
        return;
      }
      toast.success("Opportunity created");
      navigate(`/opportunities/${id}`);
    } catch (err) {
      setError(
        err instanceof Error
          ? err.message
          : "Could not create the opportunity — check the fields and try again.",
      );
    }
  }

  // The customer select is required — surface the lookup's loading/error
  // states instead of silently rendering an empty select.
  if (customersQuery.isLoading) {
    return (
      <div className="flex justify-center py-16">
        <Loader2 className="size-6 animate-spin text-[color:var(--color-text-lo)]" />
      </div>
    );
  }

  if (customersQuery.isError) {
    return (
      <EmptyState
        title="Could not load customers"
        description="The customer list failed to load, so the opportunity form can't be filled in yet."
        action={{ label: "Try again", onClick: () => customersQuery.refetch() }}
      />
    );
  }

  return (
    <EntityForm<OpportunityFormValues>
      title="Create opportunity"
      breadcrumbs={[
        { label: "Opportunities", href: "/opportunities" },
        { label: "New" },
      ]}
      defaultValues={{ stage: "lead", deal_type: "project" }}
      fields={opportunityFields(customerOptions)}
      submitLabel="Create opportunity"
      onSubmit={onSubmit}
      cancelHref="/opportunities"
      error={error}
      isSubmitting={createOpportunity.isPending}
    />
  );
}
